import type { Parcel, Rate } from '../types';

const SERVICES: Record<string, string> = {
  GROUND: 'Ground', FEDEXGROUND: 'Ground', GROUNDHOMEDELIVERY: 'Home Delivery',
  FEDEX2DAY: '2Day', FEDEX2DAYAM: '2Day A.M.', EXPRESSSAVER: 'Express Saver',
  FEDEXEXPRESSSAVER: 'Express Saver', STANDARDOVERNIGHT: 'Standard Overnight',
  PRIORITYOVERNIGHT: 'Priority Overnight', FIRSTOVERNIGHT: 'First Overnight',
  SMARTPOST: 'Ground Economy', FEDEXSMARTPOST: 'Ground Economy', GROUNDECONOMY: 'Ground Economy',
  INTERNATIONALPRIORITY: 'International Priority', INTERNATIONALECONOMY: 'International Economy',
  FEDEXFREIGHTPRIORITY: 'Freight Priority', FEDEXFREIGHTECONOMY: 'Freight Economy',
};

/** Carrier identifiers remain untouched in requests; this is display copy only. */
export function friendlyServiceName(rate: Pick<Rate, 'carrier' | 'service'> | string): string {
  const service = typeof rate === 'string' ? rate : rate.service;
  const carrier = typeof rate === 'string' ? 'FedEx' : rate.carrier;
  const key = service.replace(/[^a-z0-9]/gi, '').toUpperCase();
  const name = SERVICES[key] ?? service.replace(/^FedEx[ _-]?/i, '').replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  return `${carrier.toUpperCase().startsWith('FEDEX') ? 'FedEx' : carrier} ${name}`.trim();
}

export function deliveryDays(rate: Rate): number | null {
  const days = rate.est_delivery_days ?? rate.delivery_days;
  return typeof days === 'number' && Number.isFinite(days) && days >= 0 ? days : null;
}

export function deliveryEstimate(rate: Rate): string {
  const days = deliveryDays(rate);
  if (days === null) return 'Delivery estimate unavailable';
  if (days === 0) return 'Estimated same-day delivery';
  return `Estimated ${days} business ${days === 1 ? 'day' : 'days'}`;
}

/** Do not compare prices denominated in different currencies or missing ETAs. */
export function rateHighlights(rates: Rate[]): { cheapest: Set<string>; fastest: Set<string> } {
  const cheapest = new Set<string>();
  const fastest = new Set<string>();
  const priced = rates.filter(rate => Number.isFinite(Number(rate.rate)) && Number(rate.rate) >= 0);
  if (priced.length && new Set(priced.map(rate => rate.currency)).size === 1) {
    const minimum = Math.min(...priced.map(rate => Number(rate.rate)));
    priced.filter(rate => Number(rate.rate) === minimum).forEach(rate => cheapest.add(rate.id));
  }
  const estimated = rates.filter(rate => deliveryDays(rate) !== null);
  if (estimated.length > 1 && new Set(estimated.map(rate => deliveryDays(rate))).size > 1) {
    const minimum = Math.min(...estimated.map(rate => deliveryDays(rate)!));
    estimated.filter(rate => deliveryDays(rate) === minimum).forEach(rate => fastest.add(rate.id));
  }
  return { cheapest, fastest };
}

export function parcelSummary(parcel: Parcel): string {
  const ounces = parcel.weight;
  const weight = ounces === undefined ? 'Enter weight' : ounces % 16 === 0 ? `${ounces / 16} lb` : `${ounces} oz`;
  const dimensions = parcel.predefined_package
    ? parcel.predefined_package.replace(/([a-z])([A-Z])/g, '$1 $2').replace('Fed Ex', 'FedEx')
    : [parcel.length, parcel.width, parcel.height].every(value => value !== undefined)
      ? `${parcel.length} × ${parcel.width} × ${parcel.height} in` : 'Custom package';
  return `${weight} · ${dimensions}`;
}
